import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Leaf } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { BRAND } from '@/lib/brand';

const HERO_IMG = 'https://customer-assets.emergentagent.com/job_vegan-danish-bakery/artifacts/x8kfvzmc_bites.png';
const HERO_IMG_2 = 'https://customer-assets.emergentagent.com/job_vegan-danish-bakery/artifacts/gjjhbeqf_ChatGPT%20Image%20Jun%2024%2C%202026%2C%2001_23_35%20AM.png';
const HERO_IMG_3 = 'https://customer-assets.emergentagent.com/job_vegan-danish-bakery/artifacts/0ql7387u_2023-02-04%2017.50.02.png';

export const Hero = () => {
  return (
    <section className="relative pt-[13rem] pb-20 md:pt-[14rem] md:pb-28 overflow-hidden">
      {/* Backdrop wash */}
      <div className="absolute inset-0 bg-grain pointer-events-none opacity-60" />
      <div className="absolute -top-32 -left-32 h-[28rem] w-[28rem] rounded-full bg-leaf-soft/40 blur-3xl pointer-events-none" />
      <div className="absolute top-20 -right-32 h-[26rem] w-[26rem] rounded-full bg-honey-soft/50 blur-3xl pointer-events-none" />

      <div className="container mx-auto relative grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        {/* Copy */}
        <div className="lg:col-span-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary/70 ring-leaf text-xs font-medium text-primary"
          >
            <Leaf className="h-3.5 w-3.5 text-leaf" />
            100% plant-based · institutional foodservice
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display text-5xl sm:text-6xl lg:text-[5.25rem] leading-[1.02] font-medium text-primary mt-6 text-balance"
          >
            Plant-based <span className="italic font-light">food service</span>
            <span className="block text-leaf">solutions.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.12 }}
            className="mt-6 text-base md:text-lg leading-relaxed text-muted-foreground max-w-xl text-pretty"
          >
            We help universities, hotels and institutional dining teams solve plant-based demand —
            ready-to-serve products, consistent at scale, chef-approved.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <Button asChild size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-7 h-13 text-base shadow-card hover:shadow-pop transition-shadow">
              <Link to="/products">
                Explore the product line
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-full border-primary/25 text-primary hover:bg-secondary px-7 h-13 text-base">
              <Link to="/events">See us at CCUFSA 2026</Link>
            </Button>
          </motion.div>

          {/* Value props */}
          <motion.dl
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-12 grid grid-cols-3 gap-6 max-w-xl"
            data-testid="hero-value-props"
          >
            {[
              { v: 'Labour-light', l: 'Thaw, heat & serve — no bakery staff needed' },
              { v: 'Consistent at scale', l: 'Dependable quality across every location' },
              { v: 'Menus for everyone', l: 'Dairy-free & egg-free covers more diners' },
            ].map((s) => (
              <div key={s.v} className="border-l-2 border-leaf/40 pl-3">
                <dt className="font-display text-lg sm:text-xl font-semibold leading-snug text-primary">{s.v}</dt>
                <dd className="text-xs text-muted-foreground mt-1 leading-relaxed">{s.l}</dd>
              </div>
            ))}
          </motion.dl>
        </div>

        {/* Visual collage */}
        <div className="lg:col-span-6 relative h-[480px] sm:h-[560px] lg:h-[640px]">
          {/* Main image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-y-0 right-0 w-[78%] rounded-[2rem] overflow-hidden shadow-pop bg-secondary"
          >
            <img src={HERO_IMG} alt="Assortment of mini vegan pastries and dessert bites" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/40 via-transparent to-transparent" />
          </motion.div>

          {/* Floating chocolate croissant */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="absolute -left-2 lg:left-0 top-10 lg:top-16 w-[44%] h-[42%] rounded-3xl overflow-hidden shadow-pop bg-secondary animate-float-slow"
          >
            <img src={HERO_IMG_2} alt="Plant-based breakfast sandwich" className="h-full w-full object-cover" />
          </motion.div>

          {/* Floating muffin */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="absolute -left-4 lg:left-2 bottom-10 lg:bottom-14 w-[40%] h-[36%] rounded-3xl overflow-hidden shadow-pop bg-secondary"
            style={{ animation: 'float-slow 9s ease-in-out infinite reverse' }}
          >
            <img src={HERO_IMG_3} alt="Chocolate-glazed cream croissant" className="h-full w-full object-cover" />
          </motion.div>

          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.7, rotate: -8 }}
            animate={{ opacity: 1, scale: 1, rotate: -8 }}
            transition={{ duration: 0.7, delay: 0.5, ease: 'backOut' }}
            className="absolute right-6 -bottom-6 h-32 w-32 rounded-full bg-accent text-accent-foreground grid place-items-center shadow-amber"
          >
            <div className="text-center">
              <Sparkles className="h-5 w-5 mx-auto" />
              <div className="font-display text-sm font-semibold mt-1 leading-tight">Premium<br/>Plant-Based</div>
              <div className="text-[9px] uppercase tracking-[0.18em] mt-0.5">Est. 2010</div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Trusted partners */}
      <div className="container mx-auto mt-24 relative">
        <div className="text-center text-xs uppercase tracking-[0.22em] text-muted-foreground mb-8">Trusted by</div>
        <div className="flex flex-nowrap items-center justify-center gap-x-5 sm:gap-x-8 lg:gap-x-12 overflow-hidden">
          {[
            { src: 'https://customer-assets.emergentagent.com/job_vegan-danish-bakery/artifacts/asw8f8ml_Sheraton_Logo.png', alt: 'Sheraton Hotels - Est. 1937' },
            { src: 'https://customer-assets.emergentagent.com/job_vegan-danish-bakery/artifacts/vb2hvmia_YMCA_Canada.png', alt: 'YMCA Canada' },
            { src: 'https://customer-assets.emergentagent.com/job_vegan-danish-bakery/artifacts/z3vxi8lc_Bbud4myFgrey.png', alt: 'Ambrosia Natural Foods - Since 1979' },
            { src: 'https://customer-assets.emergentagent.com/job_vegan-danish-bakery/artifacts/uuk5r762_sweet-potato.png', alt: 'The Sweet Potato' },
            { src: 'https://customer-assets.emergentagent.com/job_vegan-danish-bakery/artifacts/wlqhn44v_logos_025-Toronto-Catholic-District-School-Board-grey.png', alt: 'Toronto Catholic District School Board' },
            { src: 'https://customer-assets.emergentagent.com/job_vegan-danish-bakery/artifacts/wlzr06mj_logos_029-York-Catholic-District-School-Board-grey.png', alt: 'York Catholic District School Board' },
          ].map((logo) => (
            <img
              key={logo.alt}
              src={logo.src}
              alt={logo.alt}
              className="h-9 sm:h-12 lg:h-14 w-auto shrink min-w-0 object-contain opacity-70 hover:opacity-100 transition-opacity duration-300"
              loading="lazy"
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
